import React from 'react';
import { DEXA_ENTRY_TIER, getDexaUpgrades, dexaSaving } from '@/data/dexaAddOn';
import { CONCIERGE_WL_ADDON } from '@/data/conciergeAddOn';
import { fmtUsd } from '@/lib/checkoutTerms';

// §checkout-upsells — the two optional pages between "choose your treatment"
// and "pay": a DEXA body-composition scan (1 scan / 3-scan bundle) and Dr.
// Mavroson's Concierge Weight Management membership. Shared by
// CatalogCheckoutStep (branded weight loss, TRT, metabolic) and
// GlpPreviewCheckoutStep (compound weight loss) so the two checkouts cannot
// drift. Each page owns its Continue/Back so PAYMENT STAYS LAST.
//
// Neither add-on is preselected: an opt-out add-on on a medical checkout is a
// charge someone did not choose.

const Check = () => (
  <svg className="w-4 h-4 text-[#2d8a5f] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const Continue = ({ onClick, testid, children }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="w-full rounded-2xl bg-[#2d8a5f] px-4 py-4 font-semibold text-white hover:bg-[#246f4d] transition-colors">
    {children}
  </button>
);
const Back = ({ onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="w-full mt-2 text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700">
    Back
  </button>
);

/**
 * DEXA scan upsell. `sessions` is the chosen tier (1 | 3) or null. Both
 * choices are visible up front — the single scan and the 3-scan bundle — and
 * tapping one selects it (tapping again deselects). Selecting REPLACES the
 * count, so the price is always the tier price, never a running sum.
 */
export const DexaUpsell = ({
  sessions, setSessions, onContinue, onBack,
  // §lab-panel-dexa: the lab panel passes its own price list + copy.
  offer,
  tiers = [DEXA_ENTRY_TIER, ...getDexaUpgrades()],
  title = "Track what you're actually losing",
  body = "A DEXA scan measures fat, muscle and bone separately — so you can see whether you're losing fat or lean mass. 15 minutes, at a partner lab.",
  singleNote,
  continueLabel = 'Continue',
}) => {
  return (
    <>
      <div className="rounded-2xl border border-gray-200 p-5 mb-4" data-testid="dexa-add-on">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-0.5">{body}</p>

        <div className="mt-3 space-y-2">
          {tiers.map((t) => {
            const sel = sessions === t.sessions;
            const bundle = t.sessions > 1;
            return (
              <button key={t.sessions} type="button" aria-pressed={sel}
                onClick={() => setSessions(sel ? null : t.sessions)}
                data-testid={bundle ? `dexa-tier-${t.sessions}` : 'dexa-add'}
                className={`relative w-full text-left rounded-xl border-2 px-4 py-3 transition-colors ${sel ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-gray-200 hover:border-[#2d8a5f]/60'}`}>
                {bundle && (
                  <span className="absolute -top-2.5 left-4 rounded-full bg-[#2d8a5f] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Best value</span>
                )}
                <span className="flex items-baseline justify-between gap-3">
                  <span className="font-medium text-gray-900">
                    {bundle ? `${t.sessions}-scan bundle` : 'Single DEXA scan'}
                    {sel && <span className="ml-2 text-xs font-semibold text-[#2d8a5f]">Added ✓</span>}
                  </span>
                  <span className="flex-shrink-0">
                    <span className="font-bold text-gray-900">{fmtUsd(t.cents)}</span>
                    {t.publicCents > t.cents && (
                      <span className="ml-2 text-sm text-gray-400 line-through">{fmtUsd(t.publicCents)}</span>
                    )}
                  </span>
                </span>
                <span className="block text-xs text-[#2d8a5f] mt-0.5">
                  {bundle
                    ? `Save ${fmtUsd(dexaSaving(t.sessions, offer))} · about ${fmtUsd(Math.round(t.cents / t.sessions))} per scan · start, mid-way and at goal`
                    : singleNote || `Save ${fmtUsd(dexaSaving(t.sessions, offer))} — this order only`}
                </span>
              </button>
            );
          })}
        </div>

        {sessions && (
          <div className="mt-3 flex items-center justify-between gap-3 text-sm">
            <span className="text-gray-600">{sessions === 1 ? 'DEXA scan added' : `${sessions}-scan bundle added`}{sessions > 1 ? ' — extra scans are held as credits; book them whenever you\'re ready.' : ''}</span>
            <button type="button" onClick={() => setSessions(null)} data-testid="dexa-remove"
              className="text-gray-500 underline underline-offset-2 hover:text-gray-700 flex-shrink-0">
              Remove
            </button>
          </div>
        )}
      </div>

      <Continue onClick={onContinue} testid="dexa-continue">
        {sessions ? continueLabel : `No thanks — ${continueLabel.toLowerCase()}`}
      </Continue>
      <Back onClick={onBack} testid="dexa-back" />
    </>
  );
};

/**
 * Concierge Weight Management upsell — Dr. Mavroson's membership at the add-on
 * price. `selected` is a boolean.
 */
export const ConciergeUpsell = ({ selected, setSelected, onContinue, onBack }) => {
  const c = CONCIERGE_WL_ADDON;
  return (
    <>
      <div className="rounded-3xl overflow-hidden border border-gray-200 mb-4" data-testid="concierge-upsell">
        <div className="relative">
          <img src={c.heroImage} alt="Concierge Weight Management — Dr. SkinnyShot + Peptides" className="w-full h-44 sm:h-52 object-cover object-top" />
          <span className="absolute top-3 left-3 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-[#2d8a5f]">Members only · New York</span>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <img src={c.doctorImage} alt={c.doctor} className="w-14 h-14 rounded-full object-cover border-2 border-white shadow" />
            <div className="min-w-0">
              <p className="font-bold text-gray-900 leading-tight">Add a doctor in your corner</p>
              <p className="text-sm text-gray-500">{c.name} with {c.doctor}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            A physician-guided membership for patients who want hands-on support while on treatment:
            direct access to Dr. Mavroson, individualized planning and member pricing on advanced testing.
          </p>
          <ul className="space-y-2 text-sm text-gray-700 mb-3">
            {c.includes.map((line) => <li key={line} className="flex gap-2"><Check /> {line}</li>)}
          </ul>
          <blockquote className="text-xs text-gray-500 italic border-l-2 border-[#2d8a5f]/40 pl-3 mb-4">
            “{c.quote}” <span className="not-italic">— {c.doctor}</span>
          </blockquote>

          {!selected ? (
            <button type="button" onClick={() => setSelected(true)} data-testid="concierge-add"
              className="w-full rounded-xl border-2 border-[#2d8a5f] bg-[#f0faf4] px-4 py-3 text-left hover:bg-[#e6f6ee] transition-colors">
              <span className="flex items-baseline justify-between gap-3">
                <span className="font-medium text-gray-900">Add concierge care</span>
                <span className="flex-shrink-0 font-bold text-gray-900">{fmtUsd(c.monthlyCents)}<span className="text-sm font-medium text-gray-500">/mo</span></span>
              </span>
              <span className="block text-xs text-[#2d8a5f] mt-0.5">First month billed with this order · cancel anytime</span>
            </button>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-xl border-2 border-[#2d8a5f] bg-[#f0faf4] px-4 py-3">
              <span className="text-sm font-medium text-gray-900">Concierge care added · {fmtUsd(c.monthlyCents)}/mo</span>
              <button type="button" onClick={() => setSelected(false)} data-testid="concierge-remove"
                className="text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 flex-shrink-0">
                Remove
              </button>
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-3">{c.footnote}</p>
        </div>
      </div>

      <Continue onClick={onContinue} testid="concierge-continue">
        {selected ? 'Continue to payment' : 'No thanks — continue to payment'}
      </Continue>
      <Back onClick={onBack} testid="concierge-back" />
    </>
  );
};

/** Order-summary rows for whichever add-ons were chosen. */
export const AddOnLineItems = ({ dexaSessions, dexaCents, conciergeCents }) => (
  <>
    {dexaCents > 0 && (
      <div className="flex justify-between text-sm mb-2" data-testid="dexa-line-item">
        <span className="text-gray-600">DEXA body scan{dexaSessions > 1 ? ` × ${dexaSessions} (bundle)` : ''}</span>
        <span className="font-medium">{fmtUsd(dexaCents)}</span>
      </div>
    )}
    {conciergeCents > 0 && (
      <div className="flex justify-between text-sm mb-2" data-testid="concierge-line-item">
        <span className="text-gray-600">Concierge Weight Management membership</span>
        <span className="font-medium">{fmtUsd(CONCIERGE_WL_ADDON.monthlyCents)}<span className="text-gray-500 font-normal">/mo</span></span>
      </div>
    )}
  </>
);
